"use client";

import { useEffect, useRef, useState, useCallback } from "react";

export interface UseIntersectionObserverOptions {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
  freezeOnceVisible?: boolean;
  initialIsIntersecting?: boolean;
}

export interface UseIntersectionObserverReturn {
  ref: (node: Element | null) => void;
  isIntersecting: boolean;
  entry: IntersectionObserverEntry | undefined;
}

/**
 * Хук для работы с Intersection Observer API
 * @param options - опции для Intersection Observer
 * @returns объект с ref и состоянием пересечения
 */
export function useIntersectionObserver(
  options: UseIntersectionObserverOptions = {},
): UseIntersectionObserverReturn {
  const {
    root = null,
    rootMargin = "0px",
    threshold = 0,
    freezeOnceVisible = false,
    initialIsIntersecting = false,
  } = options;

  const [isIntersecting, setIsIntersecting] = useState(initialIsIntersecting);
  const [entry, setEntry] = useState<IntersectionObserverEntry | undefined>();
  const [target, setTarget] = useState<Element | null>(null); // Use state for target to trigger effect

  // Stable callback ref
  const ref = useCallback((node: Element | null) => {
    setTarget(node);
  }, []);

  const thresholdKey = JSON.stringify(threshold);
  useEffect(() => {
    if (!target) return;

    // Ensure threshold is valid (it can be an array)
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        setEntry(entry);
        setIsIntersecting(entry.isIntersecting);

        if (freezeOnceVisible && entry.isIntersecting) {
          observer.disconnect();
        }
      },
      {
        root,
        rootMargin,
        threshold: JSON.parse(thresholdKey) as typeof threshold,
      },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
    // We intentionally depend on primitive values of options or assume options object is stable/memoized if passed as prop.
    // However, here we spread deps to be safe.
  }, [target, root, rootMargin, thresholdKey, freezeOnceVisible]);
  // JSON.stringify(threshold) is a simple way to handle array dependencies without deep compare hook, 
  // assuming threshold is small.

  return {
    ref,
    isIntersecting,
    entry,
  };
}

/**
 * Хук для ленивой загрузки изображений
 * @param options - опции для Intersection Observer
 * @returns объект с ref и состоянием пересечения
 */
export function useLazyLoad(options: UseIntersectionObserverOptions = {}) {
  return useIntersectionObserver({
    ...options,
    freezeOnceVisible: true,
  });
}

/**
 * Хук для бесконечной прокрутки
 * @param callback - функция вызываемая при пересечении
 * @param options - опции для Intersection Observer
 * @returns объект с ref и состоянием пересечения
 */
export function useInfiniteScroll(
  callback: () => void,
  options: UseIntersectionObserverOptions = {},
) {
  const { ref, isIntersecting } = useIntersectionObserver(options);

  useEffect(() => {
    if (isIntersecting) {
      callback();
    }
  }, [isIntersecting, callback]);

  return { ref, isIntersecting };
}

/**
 * Хук для отслеживания видимости элемента
 * @param options - опции для Intersection Observer
 * @returns объект с ref и состоянием видимости
 */
export function useVisibility(options: UseIntersectionObserverOptions = {}) {
  const { ref, isIntersecting, entry } = useIntersectionObserver(options);

  const visibility = {
    isVisible: isIntersecting,
    intersectionRatio: entry?.intersectionRatio ?? 0,
    boundingClientRect: entry?.boundingClientRect,
    rootBounds: entry?.rootBounds,
    target: entry?.target,
    time: entry?.time,
  };

  return { ref, ...visibility };
}

/**
 * Хук для анимации при появлении элемента
 * @param options - опции для Intersection Observer
 * @returns объект с ref и состоянием для анимации
 */
export function useAnimationOnScroll(
  options: UseIntersectionObserverOptions = {},
) {
  const { ref, isIntersecting } = useIntersectionObserver({
    ...options,
    threshold: 0.1,
  });

  return {
    ref,
    shouldAnimate: isIntersecting,
  };
}

/**
 * Хук для отслеживания нескольких элементов
 * @param options - опции для Intersection Observer
 * @returns объект с методами для управления наблюдением
 */
export function useMultipleIntersectionObserver(
  options: UseIntersectionObserverOptions = {},
) {
  const [intersectingElements, setIntersectingElements] = useState<
    Set<Element>
  >(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);

  const observe = useCallback(
    (element: Element) => {
      if (!observerRef.current) {
        observerRef.current = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            setIntersectingElements((prev) => {
              const newSet = new Set(prev);
              if (entry.isIntersecting) {
                newSet.add(entry.target);
              } else {
                newSet.delete(entry.target);
              }
              return newSet;
            });
          });
        }, options);
      }

      observerRef.current.observe(element);
    },
    [options],
  );

  const unobserve = useCallback((element: Element) => {
    if (observerRef.current) {
      observerRef.current.unobserve(element);
    }
  }, []);

  const disconnect = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    setIntersectingElements(new Set());
  }, []);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    observe,
    unobserve,
    disconnect,
    intersectingElements,
    isIntersecting: (element: Element) => intersectingElements.has(element),
  };
}
